/**
 * Control Presupuestario → P&L: formato de números y fechas según el idioma
 * de la plataforma (es-AR "1.505,6k", en-US "1,505.6k", pt-BR "1.505,6k") y los
 * colores del dashboard (tokens `--pnl-*` en index.css, con variante dark).
 */

import { localeFor } from '@/lib/locale';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/** Menos tipográfico (U+2212): alineado con los dígitos, no un guión. */
export const MINUS = '−';

function cap(s: string): string {
  const x = s.replace(/\.$/, '');
  return x.charAt(0).toUpperCase() + x.slice(1);
}

export interface PnlFmt {
  locale: string;
  num: (v: number, d?: number) => string;
  sgn: (v: number, d?: number) => string;
  /** USD → miles con unidad: 1505600 → "1.505,6k". */
  k: (usd: number, d?: number) => string;
  sgnK: (usd: number, d?: number) => string;
  pct: (v: number | null, d?: number) => string;
  sgnPct: (v: number | null, d?: number) => string;
  pp: (v: number, d?: number) => string;
  /**
   * Chip de variación. Un % sobre una base cercana a cero explota y no dice nada
   * (EBITDA de 4k a −116k son −3.012 %): por encima de 300 % va la diferencia en k.
   */
  delta: (pct: number | null, absUsd: number, pp: boolean) => string;
  monthShort: (m: number) => string;
  monthLong: (m: number) => string;
  dateTime: (iso: string) => string;
}

export function makeFmt(locale: string, year: number): PnlFmt {
  const cache = new Map<number, Intl.NumberFormat>();
  const nf = (d: number) => {
    let x = cache.get(d);
    if (!x) {
      x = new Intl.NumberFormat(locale, { minimumFractionDigits: d, maximumFractionDigits: d });
      cache.set(d, x);
    }
    return x;
  };
  const num = (v: number, d = 1) => (v < 0 ? MINUS : '') + nf(d).format(Math.abs(v));
  const sgn = (v: number, d = 1) => (v > 0 ? '+' : v < 0 ? MINUS : '') + nf(d).format(Math.abs(v));
  const short = new Intl.DateTimeFormat(locale, { month: 'short' });
  const long = new Intl.DateTimeFormat(locale, { month: 'long' });
  const dt = new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' });
  const bad = (v: number | null): v is null => v === null || !Number.isFinite(v);
  return {
    locale,
    num,
    sgn,
    k: (usd, d = 1) => `${num(usd / 1000, d)}k`,
    sgnK: (usd, d = 1) => `${sgn(usd / 1000, d)}k`,
    pct: (v, d = 1) => (bad(v) ? '–' : `${num(v, d)}%`),
    sgnPct: (v, d = 1) => (bad(v) ? '–' : `${sgn(v, d)}%`),
    pp: (v, d = 1) => `${sgn(v, d)}pp`,
    delta: (pct, absUsd, pp) => {
      if (pp) return `${sgn(pct ?? 0, 1)}pp`;
      if (bad(pct) || Math.abs(pct) > 300) return `${sgn(absUsd / 1000, 1)}k`;
      return `${sgn(pct, 1)}%`;
    },
    monthShort: (m) => cap(short.format(new Date(year, m - 1, 1))),
    monthLong: (m) => cap(long.format(new Date(year, m - 1, 1))),
    dateTime: (iso) => {
      const t = Date.parse(iso);
      return Number.isFinite(t) ? dt.format(new Date(t)) : iso;
    },
  };
}

export function usePnlFmt(year: number): PnlFmt {
  const { i18n } = useTranslation();
  const lang = (i18n.language ?? 'es').slice(0, 2);
  return useMemo(() => makeFmt(localeFor(lang), year), [lang, year]);
}

export type PayMode = 'nonPayroll' | 'payroll' | 'total';

/** Opciones del toggle Payroll / Non-payroll / Total, con los textos de `budget_control.pay.*`. Vive acá y no en pnl-ui (solo componentes) por la regla react-refresh del lint. */
export function usePayOptions(): { options: Array<{ value: PayMode; label: string }>; label: (pay: PayMode) => string; longLabel: (pay: PayMode) => string; sbLabel: string } {
  const { t } = useTranslation();
  const label = (pay: PayMode) => t(pay === 'payroll' ? 'budget_control.pay.payroll' : pay === 'total' ? 'budget_control.pay.total' : 'budget_control.pay.nonpayroll');
  return {
    options: (['nonPayroll', 'payroll', 'total'] as PayMode[]).map((value) => ({ value, label: label(value) })),
    label,
    longLabel: (pay) => (pay === 'total' ? t('budget_control.pay.total_long') : label(pay).toLowerCase()),
    sbLabel: t('budget_control.pay.sb_label'),
  };
}

/** Tokens de color (index.css). Se usan como `style`, no como clases: los SVG no leen clases de Tailwind para fill. */
export const C = {
  fav: 'var(--pnl-fav)',
  favSoft: 'var(--pnl-fav-soft)',
  unfav: 'var(--pnl-unfav)',
  unfavSoft: 'var(--pnl-unfav-soft)',
  neutral: 'var(--pnl-neutral)',
  warn: 'var(--pnl-warn)',
  hair: 'var(--pnl-hair)',
  hairStrong: 'var(--pnl-hair-strong)',
  ink: 'var(--pnl-ink)',
  ink2: 'var(--pnl-ink-2)',
  ink3: 'var(--pnl-ink-3)',
  surface2: 'var(--pnl-surface-2)',
} as const;

/** Color de un desvío: gris si es despreciable, azul si favorable, rojo si no. */
export function deltaColor(favorable: boolean, value: number, threshold = 50): string {
  if (Math.abs(value) < threshold) return C.hairStrong;
  return favorable ? C.fav : C.unfav;
}
